import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import MobileNavigation from "../../components/ui/MobileNavigation";
import ProductCard from "./components/ProductCard";
import ComboPackCard from "./components/ComboPackCard"; // ✅ New import
import ProductToolbar from "./components/ProductToolbar";
import ProductModal from "./components/ProductModal";
import ProductDetailsModal from "./components/ProductDetailsModal";
import BulkActionModal from "./components/BulkActionModal";
import Button from "../../components/ui/Button";
import UseProductHook from "../../hooks/UseProductHook";
import UseComboPackHook from "../../hooks/UseComboPackHook";
import ComboModal from "./components/ComboModal";

const ProductManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // State
  const [products, setProducts] = useState([]);
  const [comboPacks, setComboPacks] = useState([]);
  const [activeTab, setActiveTab] = useState("products");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");

  const [productModal, setProductModal] = useState({ isOpen: false, product: null, mode: "add" });
  const [comboModal, setComboModal] = useState({ isOpen: false, combo: null, mode: "add" });
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, product: null });
  const [bulkActionModal, setBulkActionModal] = useState({ isOpen: false, action: "", products: [] });

  const { product } = useSelector((state) => state.product);
  const { comboPack } = useSelector((state) => state.comboPack);

  const { getAllProduct } = UseProductHook();
  const { getAllComboPacks, deleteComboPack } = UseComboPackHook(); // ✅ Include delete

  // ✅ Load user, products, and combo packs
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      // if (parsedUser.role !== "vendor") {
      //   navigate("/whats-app-authentication");
      //   return;
      // }
    } else {
      navigate("/whats-app-authentication");
      return;
    }

    // Load data
    const loadData = async () => {
      await getAllProduct();
      const combos = await getAllComboPacks();
      if (combos) setComboPacks(combos);
    };

    loadData();
  }, [navigate]);

  useEffect(() => {
    if (product) setProducts(product);
  }, [product]);
  useEffect(() => {
    if (comboPack) setComboPacks(comboPack);
  }, [comboPack]);

  // console.log("✅ Combo Packs:", comboPacks);

  // ✅ Filter + Sort for Products
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.category && item.category.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
      const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
      return matchesSearch && matchesCategory && matchesStatus;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "price_asc":
          return a.price - b.price;
        case "price_desc":
          return b.price - a.price;
        case "date_asc":
          return new Date(a.createdAt) - new Date(b.createdAt);
        case "date_desc":
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, searchQuery, selectedCategory, selectedStatus, sortBy]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ✅ Handlers
  const handleAddProduct = () => {
    setProductModal({ isOpen: true, product: null, mode: "add" });
  };

  const handleAddCombo = () => {
    setComboModal({ isOpen: true, combo: null, mode: "add" });
  };

  const handleEditProduct = (item) => {
    if (item.type === "combo") {
      setComboModal({ isOpen: true, combo: item, mode: "edit" });
    } else {
      setProductModal({ isOpen: true, product: item, mode: "edit" });
    }
  };

  const handleViewDetails = (item) => {
    setDetailsModal({ isOpen: true, product: item });
  };

  const handleDeleteCombo = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this combo pack?");
    if (confirmed) {
      const success = await deleteComboPack(id);
      if (success) {
        const updated = comboPacks.filter((combo) => combo._id !== id);
        setComboPacks(updated);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
      />
      <Sidebar
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <MobileNavigation user={user} isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
        }`}
      >
        <div className="p-6">
          {user && user.role === "vendor" && (
            
          <Breadcrumb user={user} />
          )}
          {user && user.role === "admin" && (
            <BreadcrumbAdmin user={user} />
          )}

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Product & Combo Management
              </h1>
              <p className="text-muted-foreground">Manage your products and combo packs</p>
            </div>
          </div>

          {/* ✅ Tabs */}
          <div className="flex space-x-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("products")}
              className={`pb-2 px-4 text-sm font-medium ${
                activeTab === "products"
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setActiveTab("combos")}
              className={`pb-2 px-4 text-sm font-medium ${
                activeTab === "combos"
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Combo Packs
            </button>
          </div>

          {/* ✅ Toolbar */}
          <ProductToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onAddProduct={activeTab === "products" ? handleAddProduct : handleAddCombo}
          />

          {/* ✅ Tab Content */}
          {activeTab === "products" ? (
            filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((item) => (
                  <ProductCard
                    key={item._id}
                    product={item}
                    onEdit={handleEditProduct}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-foreground mb-2">No products found</h3>
                <Button onClick={handleAddProduct} iconName="Plus" iconPosition="left" iconSize={16}>
                  Add New Product
                </Button>
              </div>
            )
          ) : comboPacks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {comboPacks.map((combo) => (
                <ComboPackCard
                  key={combo._id}
                  combo={combo}
                  onEdit={() => handleEditProduct({ ...combo, type: "combo" })}
                  onViewDetails={handleViewDetails}
                  onDelete={handleDeleteCombo}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold text-foreground mb-2">No combo packs found</h3>
              <Button onClick={handleAddCombo} iconName="Plus" iconPosition="left" iconSize={16}>
                Add New Combo Pack
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* ✅ Modals */}
      <ProductModal
        isOpen={productModal.isOpen}
        onClose={() => setProductModal({ isOpen: false, product: null, mode: "add" })}
        product={productModal.product}
        mode={productModal.mode}
        onSave={() => {}}
      />

      <ComboModal
        isOpen={comboModal.isOpen}
        onClose={() => setComboModal({ isOpen: false, combo: null, mode: "add" })}
        combo={comboModal.combo}
        mode={comboModal.mode}
        onSave={() => {}}
      />

      <ProductDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, product: null })}
        product={detailsModal.product}
        onEdit={handleEditProduct}
      />

      <BulkActionModal
        isOpen={bulkActionModal.isOpen}
        onClose={() => setBulkActionModal({ isOpen: false, action: "", products: [] })}
        selectedProducts={bulkActionModal.products}
        action={bulkActionModal.action}
        onConfirm={() => {}}
      />
    </div>
  );
};

export default ProductManagement;
